/**
 * ExportsPage — Phase 3C.
 *
 * Filters the portfolio bordereau (date range, broker, risk band), previews
 * the row count + sample rows, and downloads CSV / XLSX with the same
 * filter payload. The audit log records every download.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input, Label, Select } from '@/components/ui/input';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, FileText, Filter, RefreshCcw } from 'lucide-react';

interface BrokerLite { id: string; brokerage_name: string; }

interface PreviewColumn { key: string; header: string; }
interface PreviewRow { [key: string]: string | number; }
interface PreviewResponse {
  row_count: number;
  columns: PreviewColumn[];
  sample: PreviewRow[];
  filters: Record<string, string | null>;
}

const BAND_OPTIONS = ['', 'low', 'medium', 'high', 'critical'] as const;

const BAND_LABEL: Record<string, string> = {
  low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical', '': 'All bands',
};

const BAND_BADGE: Record<string, 'success' | 'warning' | 'destructive' | 'muted'> = {
  low: 'success', medium: 'warning', high: 'destructive', critical: 'destructive',
};

export const ExportsPage: React.FC = () => {
  const [brokers, setBrokers] = useState<BrokerLite[]>([]);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [broker, setBroker] = useState<string>('');
  const [band, setBand] = useState<string>('');
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [downloading, setDownloading] = useState<'csv' | 'xlsx' | null>(null);

  // Fetch brokerages once — they seed the filter dropdown.
  useEffect(() => {
    api.get<Array<{ id: string; brokerage_name: string }>>('/brokers').then((r) => {
      setBrokers(r.data.map((b) => ({ id: b.id, brokerage_name: b.brokerage_name })));
    }).catch(() => setBrokers([]));
  }, []);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    if (dateFrom) p.set('date_from', dateFrom);
    if (dateTo) p.set('date_to', dateTo);
    if (broker) p.set('broker_id', broker);
    if (band) p.set('band', band);
    return p;
  }, [dateFrom, dateTo, broker, band]);

  const loadPreview = async (): Promise<void> => {
    setLoading(true);
    try {
      const r = await api.get<PreviewResponse>(`/exports/bordereau/preview?${queryParams.toString()}`);
      setPreview(r.data);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to load preview.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPreview(); /* eslint-disable-next-line */ }, [dateFrom, dateTo, broker, band]);

  const resetFilters = (): void => {
    setDateFrom(''); setDateTo(''); setBroker(''); setBand('');
  };

  const download = async (fmt: 'csv' | 'xlsx'): Promise<void> => {
    setDownloading(fmt);
    try {
      const url = `/exports/bordereau.${fmt}?${queryParams.toString()}`;
      const r = await api.get(url, { responseType: 'blob' });
      const cd = (r.headers['content-disposition'] || r.headers['Content-Disposition'] || '') as string;
      const match = /filename\s*=\s*"([^"]+)"/i.exec(cd);
      const filename = match?.[1] ?? `raptor-bordereau.${fmt}`;
      const blob = r.data as Blob;
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
      const rows = r.headers['x-row-count'] ?? preview?.row_count ?? '';
      toast.success(`Downloaded ${filename}${rows !== '' ? ` — ${rows} row(s)` : ''}.`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Download failed.';
      toast.error(msg);
    } finally {
      setDownloading(null);
    }
  };

  const previewColumns = preview?.columns ?? [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="exports-page">
      <div>
        <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Bordereaux Exports</h2>
        <p className="text-sm text-raptor-navy/60 font-body">Generate CSV or XLSX bordereaux for the portfolio. Every download is captured in the audit log.</p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Filter size={18} /> Filters</CardTitle>
            <CardDescription>Narrow the export by date range, broker, or risk band.</CardDescription>
          </div>
          <Button size="sm" variant="ghost" onClick={resetFilters} data-testid="reset-filters-btn">
            <RefreshCcw size={14} /> Reset
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Date from</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
            </div>
            <div>
              <Label>Date to</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
            </div>
            <div>
              <Label>Broker</Label>
              <Select value={broker} onChange={(e) => setBroker(e.target.value)} data-testid="filter-broker">
                <option value="">All brokers</option>
                {brokers.map((b) => (
                  <option key={b.id} value={b.id}>{b.brokerage_name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Risk band</Label>
              <Select value={band} onChange={(e) => setBand(e.target.value)} data-testid="filter-band">
                {BAND_OPTIONS.map((b) => (
                  <option key={b} value={b}>{BAND_LABEL[b]}</option>
                ))}
              </Select>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-raptor-navy/70">
            <span className="font-body uppercase text-[10px] text-raptor-navy/60">Active:</span>
            {dateFrom && <Badge variant="outline">from {dateFrom}</Badge>}
            {dateTo && <Badge variant="outline">to {dateTo}</Badge>}
            {broker && <Badge variant="outline">{brokers.find((b) => b.id === broker)?.brokerage_name ?? broker}</Badge>}
            {band && <Badge variant={BAND_BADGE[band] ?? 'outline'}>{BAND_LABEL[band]}</Badge>}
            {!dateFrom && !dateTo && !broker && !band && <span className="italic text-raptor-navy/50">No filters — exports the entire portfolio</span>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Preview</CardTitle>
            <CardDescription>Row count matches what will land in the CSV/XLSX. Sample shown below.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-[10px] uppercase text-raptor-navy/60 font-body">Rows</div>
              <div className="font-tabular text-2xl font-bold text-raptor-navy" data-testid="preview-row-count">
                {loading ? '…' : preview?.row_count ?? 0}
              </div>
            </div>
            <Button
              variant="outline" size="sm"
              onClick={() => download('csv')}
              disabled={downloading !== null || loading || (preview?.row_count ?? 0) === 0}
              data-testid="download-csv-btn"
            >
              <FileText size={14} className={downloading === 'csv' ? 'animate-pulse' : ''} /> Download CSV
            </Button>
            <Button
              variant="primary" size="sm"
              onClick={() => download('xlsx')}
              disabled={downloading !== null || loading || (preview?.row_count ?? 0) === 0}
              data-testid="download-xlsx-btn"
            >
              <FileSpreadsheet size={14} className={downloading === 'xlsx' ? 'animate-pulse' : ''} /> Download XLSX
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading && (
            <div className="p-6 text-sm text-raptor-navy/60">Loading preview…</div>
          )}
          {!loading && preview && preview.row_count === 0 && (
            <div className="p-6 text-sm text-raptor-navy/60 bg-raptor-neutral/30 m-6 rounded-md">
              No assessments match these filters. Adjust the filters, or run "Recompute all" on the Risk Ratings page if you have new submitted results.
            </div>
          )}
          {!loading && preview && preview.row_count > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
                  <tr>
                    {previewColumns.map((c) => (
                      <th key={c.key} className="py-2 px-3 text-left whitespace-nowrap font-body">{c.header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.map((row, ix) => (
                    <tr key={ix} className="border-t border-border/60">
                      {previewColumns.map((c) => {
                        const raw = row[c.key];
                        const value = raw === null || raw === undefined ? '' : String(raw);
                        const cellClass = ['raw_score', 'max_possible', 'normalised_score', 'effective_score', 'template_version']
                          .includes(c.key) ? 'font-tabular text-right' : 'font-body';
                        // Highlight band cells with a mini badge
                        if (c.key === 'effective_band' || c.key === 'computed_band') {
                          const b = value.toLowerCase();
                          return (
                            <td key={c.key} className="py-2 px-3">
                              {b ? <Badge variant={BAND_BADGE[b] ?? 'outline'}>{BAND_LABEL[b] ?? value}</Badge> : ''}
                            </td>
                          );
                        }
                        return (
                          <td key={c.key} className={`py-2 px-3 ${cellClass} truncate max-w-[240px]`}>{value}</td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.row_count > preview.sample.length && (
                <div className="p-3 text-center text-xs text-raptor-navy/60 border-t border-border/60 bg-raptor-neutral/20">
                  Showing {preview.sample.length} of {preview.row_count}. Download to see all rows.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Download size={14} /> About bordereaux exports</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-raptor-navy/70 space-y-2 font-body">
          <p>Each row represents one risk assessment. When a rating has been overridden by an underwriter, the effective band + score reflect the override and the reason is captured alongside the actor and timestamp.</p>
          <p>The XLSX file includes a second <em>Summary</em> sheet with totals by band and by brokerage — useful when circulating the bordereau to reinsurers or reporting stakeholders.</p>
          <p>Downloads are audit-logged. To review historical exports, open the Audit Log and filter by action <span className="font-tabular text-xs bg-raptor-neutral/40 px-1 py-0.5 rounded">export_bordereau</span>.</p>
        </CardContent>
      </Card>
    </div>
  );
};
